import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

import SEO from '../components/SEO';
import '../styles/Auth.css';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenHash = params.get('token_hash');
        const type = params.get('type') || 'recovery';

        if (tokenHash) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type,
          });

          if (error) {
            setError('This reset link is invalid or has expired. Please request a new one.');
          } else {
            setSessionReady(true);
          }
        } else {
          // Check if there's already a session (from hash fragment)
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setSessionReady(true);
          } else {
            setError('Invalid reset link. Please request a new password reset.');
          }
        }
      } catch (err) {
        setError('Something went wrong. Please request a new password reset.');
      } finally {
        setVerifying(false);
      }
    };

    const timer = setTimeout(verifyToken, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError('Failed to update password. Please try again.');
      } else {
        // Sign out after password reset so user logs in fresh
        await supabase.auth.signOut();
        window.sessionStorage.clear();
        setSuccess(true);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <SEO title="Password Reset" path="/reset-password" noindex={true} />
        <div className="auth-left">
          <div className="auth-logo">
            <Link to="/" className="auth-logo-link">
              <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
              <span className="brand-name">pathproxy</span>
            </Link>
          </div>

          <div className="auth-form-container">
            <div className="success-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E8FF00" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
              <h1>Password Updated!</h1>
              <p>Your password has been successfully changed.</p>
              <p className="auth-subtitle">You can now log in with your new password.</p>
              <Link to="/login" className="auth-submit-btn" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '20px' }}>
                Go to Login
              </Link>
            </div>
          </div>
        </div>

        <div className="auth-right">
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SEO title="Reset Password" path="/reset-password" noindex={true} />
      <div className="auth-left">
        <div className="auth-logo">
          <Link to="/" className="auth-logo-link">
            <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
            <span className="brand-name">pathproxy</span>
          </Link>
        </div>

        <div className="auth-form-container">
          {verifying ? (
            <div className="success-message">
              <div className="btn-spinner" style={{ width: '48px', height: '48px', margin: '0 auto 24px' }}></div>
              <h1>Verifying...</h1>
              <p>Please wait while we verify your reset link.</p>
            </div>
          ) : !sessionReady ? (
            <div className="success-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ff4444" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <h1>Link Expired</h1>
              <p>{error || 'This reset link is invalid or has expired.'}</p>
              <Link to="/forgot-password" className="auth-submit-btn" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '20px' }}>
                Request New Link
              </Link>
            </div>
          ) : (
            <>
              <h1>Set New Password</h1>
              <p className="auth-subtitle">Enter your new password below</p>

              {error && (
                <div className="auth-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label>New Password <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      placeholder="Min 8 chars, uppercase, lowercase, number"
                      required
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={128}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Confirm New Password <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      placeholder="Confirm your new password"
                      required
                      autoComplete="new-password"
                      maxLength={128}
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="auth-right">
      </div>
    </div>
  );
};

export default ResetPasswordPage;
